/**
 * Play Families age gate — Android-only.
 *
 * Google Play requires a neutral age question at first launch for apps
 * that declare a target audience including children under 13. This is
 * that gate. Not applicable to iOS (we are not in Apple Kids Category)
 * or to the web app.
 *
 * The gate is one-shot: once answered, we remember the answer in
 * localStorage and never ask again for this install. We don't remember
 * the exact age on disk in the gate record — only a flag. The child_age
 * pre-fill lives in a separate key so deleting it doesn't un-gate the
 * app.
 *
 * A "<13" answer is additionally remembered as a child-declared install
 * (CHILD_KEY): Play's Families policy forbids handing children SDKs that
 * aren't approved for child-directed use — Google Sign-In is explicitly on
 * that list — so lib/googleAuth.ts hides the Google button while the flag
 * is set. Email registration by the parent always works.
 *
 * Parental gate (2026-09-05): the parent who takes over can pass a
 * multiplication gate (lib/parentGateCore.ts). Passing it records a
 * short-lived "parent verified" mark in sessionStorage (PARENT_KEY, 15 min,
 * dies with the app session) and Google Sign-In shows again for that time —
 * the account being created is the parent's. The child flag itself never
 * changes, so the logged-out experience stays child-directed.
 */
import { isNativeApp, getPlatform } from './platform';
import { PARENT_VERIFIED_TTL_MS, isParentVerified } from './parentGateCore';

const GATE_KEY = 'snugtale_age_gate_v1';
const PREFILL_KEY = 'snugtale_age_prefill';
const CHILD_KEY = 'snugtale_age_gate_child';
const PARENT_KEY = 'snugtale_parent_gate_until';

/**
 * True when the Play Families age gate applies to this session.
 * Android native only — the gate is a Play Store requirement, not an
 * Apple or web one.
 */
export function ageGateApplies(): boolean {
  return isNativeApp() && getPlatform() === 'android';
}

export function ageGateAnswered(): boolean {
  try {
    return localStorage.getItem(GATE_KEY) === 'passed';
  } catch {
    return false;
  }
}

export function recordAgeGateAnswer(age: number): void {
  try {
    localStorage.setItem(GATE_KEY, 'passed');
    if (age < 13) {
      // Pre-fill signal for RegisterPage — the adult taking over will
      // see child_age already populated from the kid's answer.
      localStorage.setItem(PREFILL_KEY, String(age));
      localStorage.setItem(CHILD_KEY, '1');
    } else {
      localStorage.removeItem(PREFILL_KEY);
      localStorage.removeItem(CHILD_KEY);
    }
  } catch {
    // ignore storage failures
  }
}

/** Returns the pre-filled child age if the kid answered <13, else null. */
export function consumeChildAgePrefill(): number | null {
  try {
    const raw = localStorage.getItem(PREFILL_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 && n < 13 ? n : null;
  } catch {
    return null;
  }
}

/**
 * True when this Android install answered the neutral age screen with <13.
 * Gates SDKs that Play does not approve for children (Google Sign-In).
 * Always false on iOS and web, where the gate does not apply.
 */
export function ageGateChildDeclared(): boolean {
  if (!ageGateApplies()) return false;
  try {
    return localStorage.getItem(CHILD_KEY) === '1';
  } catch {
    return false;
  }
}

/** The parent passed the gate within the last 15 minutes of this app session. */
export function parentVerified(): boolean {
  try {
    const raw = sessionStorage.getItem(PARENT_KEY);
    return isParentVerified(raw ? Number(raw) : null);
  } catch {
    return false;
  }
}

export function recordParentVerified(now: number = Date.now()): void {
  try {
    sessionStorage.setItem(PARENT_KEY, String(now + PARENT_VERIFIED_TTL_MS));
  } catch {
    // ignore storage failures
  }
}

/** Child-declared install and no fresh parent verification: Google stays hidden and the unlock link shows. */
export function parentGateNeeded(): boolean {
  return ageGateChildDeclared() && !parentVerified();
}
